// Which face each admin-PWA card shows. Pure: the cards read the browser, these
// decide. Order matters in both — the first rule that applies wins.

// ─── « Install the app » ────────────────────────────────────────────────────

export interface InstallEnv {
  /** Already running as the installed app. */
  standalone: boolean;
  /** The owner closed the card (remembered in localStorage). */
  dismissed: boolean;
  /** The browser said `appinstalled` during this visit. */
  installed: boolean;
  ios: boolean;
  /** Chrome handed us a `beforeinstallprompt` we can replay on a tap. */
  canPrompt: boolean;
}

export type InstallCardState = 'hidden' | 'prompt' | 'ios-steps';

export function installCardState(e: InstallEnv): InstallCardState {
  if (e.standalone || e.dismissed || e.installed) return 'hidden';
  if (e.canPrompt) return 'prompt';
  // iPhone has no install prompt at all — the only way is the Share sheet.
  if (e.ios) return 'ios-steps';
  // Chrome has not said it can install (yet), or this browser cannot: offering
  // a button that does nothing would be worse than no card.
  return 'hidden';
}

// ─── « Turn on notifications » ──────────────────────────────────────────────

export interface PushEnv {
  ios: boolean;
  standalone: boolean;
  /** Service worker + PushManager + Notification on a secure page. */
  supported: boolean;
  permission: NotificationPermission;
  /** This device has a push subscription. */
  subscribed: boolean;
}

export type PushCardState = 'needs-install' | 'unsupported' | 'working' | 'blocked' | 'on' | 'error' | 'off';

export function pushCardState(e: PushEnv, ui: { busy: boolean; error: string | null }): PushCardState {
  // ⚠️ FIRST, BEFORE « supported »: in a Safari tab an iPhone has no PushManager
  // at all, so it would read as « not supported » — when the real answer is
  // « install the app, then open it from the icon » (iOS 16.4+).
  if (e.ios && !e.standalone) return 'needs-install';
  if (!e.supported) return 'unsupported';
  if (ui.busy) return 'working';
  // Denied is final from the page's side: only the phone's settings undo it,
  // so a retry button would lie.
  if (e.permission === 'denied') return 'blocked';
  if (e.permission === 'granted' && e.subscribed) return 'on';
  if (ui.error) return 'error';
  return 'off';
}
